import fetchWithAuth from "../utils/fetchWithAuth";
import { getEndpoints, buildEndpoint } from "../utils/ApiContext";

export const getGroups = async (username = null) => {
  await getEndpoints();
  
  let url = await buildEndpoint("API_GROUPS");
  
  if (username) {
    url += `?username=${encodeURIComponent(username)}`;
  }
  
  const resp = await fetchWithAuth(url);

  if (!resp.ok) {
    const errorText = await resp.text();
    throw new Error(`Error al obtener grupos (${resp.status}): ${errorText || resp.statusText}`);
  }
  
  const groups = await resp.json();
  
  return groups.map(group => ({
    ...group,
    users: []
  }));
};

export const getGroup = async (groupname) => {
  if (!groupname) {
    throw new Error("El nombre del grupo es requerido");
  }

  await getEndpoints();
  const url = await buildEndpoint("API_GROUPS", groupname);
  const resp = await fetchWithAuth(url);

  if (!resp.ok) {
    const errorText = await resp.text();
    if (resp.status === 404) {
      throw new Error(`Grupo '${groupname}' no encontrado`);
    }
    throw new Error(`Error al obtener grupo (${resp.status}): ${errorText || resp.statusText}`);
  }
  
  return resp.json();
};

export const createGroup = async (groupData) => {
  if (!groupData || !groupData.groupname) {
    throw new Error("El nombre del grupo es requerido");
  }

  await getEndpoints();
  const url = await buildEndpoint("API_GROUPS");
  
  const payload = {
    groupname: groupData.groupname,
    ...(groupData.description && { description: groupData.description })
  };
  
  const resp = await fetchWithAuth(url, {
    method: "POST",
    body: JSON.stringify(payload)
  });

  if (!resp.ok) {
    const errorText = await resp.text();
    if (resp.status === 409) {
      throw new Error(`El grupo '${groupData.groupname}' ya existe`);
    }
    throw new Error(`Error al crear grupo (${resp.status}): ${errorText || resp.statusText}`);
  }
  
  return resp.json();
};

export const updateGroup = async (groupname, updateData = {}) => {
  if (!groupname) {
    throw new Error("El nombre del grupo es requerido");
  }

  await getEndpoints();
  const url = await buildEndpoint("API_GROUPS", groupname);
    
  const payload = {};
  
  if (updateData.description !== undefined) {
    payload.description = updateData.description;
  }
  
  if (updateData.users) {
    payload.users = updateData.users;
  }
  
  const resp = await fetchWithAuth(url, {
    method: "PUT",
    body: JSON.stringify(payload)
  });

  if (!resp.ok) {
    const errorText = await resp.text();
    if (resp.status === 404) {
      throw new Error(`Grupo '${groupname}' no encontrado`);
    }
    throw new Error(`Error al actualizar grupo (${resp.status}): ${errorText || resp.statusText}`);
  }
  
  return;
};

export const deleteGroup = async (groupname) => {
  if (!groupname) {
    throw new Error("El nombre del grupo es requerido");
  }

  await getEndpoints();
  const url = await buildEndpoint("API_GROUPS", groupname);
  
  const resp = await fetchWithAuth(url, {
    method: "DELETE"
  });

  if (!resp.ok) {
    const errorText = await resp.text();
    if (resp.status === 404) {
      throw new Error(`Grupo '${groupname}' no encontrado`);
    }
    throw new Error(`Error al eliminar grupo (${resp.status}): ${errorText || resp.statusText}`);
  }
  
  return;
};

export const addUserToGroup = async (groupname, username) => {
  if (!groupname || !username) {
    throw new Error("Tanto el nombre del grupo como el del usuario son requeridos");
  }
  
  return updateGroup(groupname, {
    users: { [username]: true }
  });
};

export const removeUserFromGroup = async (groupname, username) => {
  if (!groupname || !username) {
    throw new Error("Tanto el nombre del grupo como el del usuario son requeridos");
  }
  
  return updateGroup(groupname, {
    users: { [username]: false }
  });
};

export const getGroupUsers = async (groupname) => {
  if (!groupname) {
    throw new Error("El nombre del grupo es requerido");
  }

  try {
    await getEndpoints();
    const url = await buildEndpoint("API_USERGET") + `?groupname=${encodeURIComponent(groupname)}`;
    
    const resp = await fetchWithAuth(url);
    
    if (!resp.ok) {
      const errorText = await resp.text();
      return [];
    }
    
    const users = await resp.json();
    
    if (Array.isArray(users)) {
      return users;
    }
    
    return [];
  } catch (error) {
    return [];
  }
};

export const removeUsersFromGroup = async (groupname, usernames) => {
  if (!groupname || !Array.isArray(usernames) || usernames.length === 0) {
    throw new Error("El nombre del grupo y un array de usuarios son requeridos");
  }
  
  const users = usernames.reduce((acc, username) => {
    acc[username] = false;
    return acc;
  }, {});
  
  return updateGroup(groupname, { users });
};